import { useQuery } from "@tanstack/react-query";
import {
  getPaginatedAssignedUsers,
  getPaginatedReferralAgents,
} from "@/modules/common/api/index.v2";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  MappedUserData,
  UserQueryParams,
  UsersList,
  UserStatus,
} from "@/modules/common/dtos/index.v2";
import { queryCommonFlags } from "@/api/constants";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { UsersOrderByOptions } from "@/modules/user/api/types";
import { OrderDirection } from "@/api/types";
//TODO: Update this type to match the UserResponse type in the API response
interface UserSearchParams {
  currentPage?: string;
  initialParams?: Partial<UserQueryParams>;
  queryKey?: string;
  refetchInterval?: number;
  organizationUId?: string;
  enabled?: boolean;
  referralAgents?: boolean;
}

const useUsersData = ({
  currentPage,
  initialParams,
  queryKey,
  refetchInterval,
  organizationUId,
  enabled = true,
  referralAgents = false,
}: UserSearchParams = {}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [usersSearchTerm, setUsersSearchTerm] = useState("");
  const [rankFilterUids, setRankFilterUids] = useState<string[]>([]);
  const [showBanned, setShowBanned] = useState(false);
  const [debouncedSearchTerm] = useDebounce(usersSearchTerm, 300);
  const [debouncedRankFilterUids] = useDebounce(rankFilterUids, 300);
  const storedOrgName = localStorage.getItem(`selectedOrganizationName`);
  const homeLocalStorageFilters = localStorage.getItem(
    `leadSearchFilters${storedOrgName}`
  );

  const parsedFilters = homeLocalStorageFilters
    ? JSON.parse(homeLocalStorageFilters)
    : {};

  const nextActionsLocalStorageFilters = localStorage.getItem(
    `nextActionsSearchFilters${storedOrgName}`
  );

  const parsedFiltersNextActions = nextActionsLocalStorageFilters
    ? JSON.parse(nextActionsLocalStorageFilters)
    : {};

  const path = window.location.pathname;

  const handlePageChange = (page: number) => {
    setPage(page);
    updateParams({ page });
  };

  const { selectedSingleOrganization } = useOrganization();
  useEffect(() => {
    handlePageChange(1);
    handlePageSizeChange(50);
    setUsersSearchTerm("");
    setRankFilterUids([]);
  }, [selectedSingleOrganization]);

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
    updateParams({ pageSize, page: 1 });
  };

  const [queryParams, setQueryParams] = useState<UserQueryParams>({
    page,
    pageSize,
    ...(parsedFilters?.userUIds &&
      currentPage === "Home" && {
        savedUIds: path.includes("next-actions")
          ? parsedFiltersNextActions?.userUIds
          : parsedFilters?.userUIds,
      }),
    ...(parsedFilters?.supervisorUIds &&
      initialParams?.minRankValue === 2 &&
      currentPage === "Home" && {
        savedUIds: path.includes("next-actions")
          ? parsedFiltersNextActions?.supervisorUIds
          : parsedFilters?.supervisorUIds,
      }),
    orderBy: UsersOrderByOptions.RANK,
    orderDirection: OrderDirection.DESCENDING,
    ...initialParams,
    ...(initialParams?.statuses && {
      statuses: [
        ...(initialParams?.statuses || []),
        ...(showBanned ? [UserStatus.Banned] : []),
      ],
    }),
  });

  const handleShowBannedChange = () => {
    const newShowBanned = !showBanned;
    setShowBanned(newShowBanned);

    if (newShowBanned) {
      setQueryParams((prev) => ({
        ...prev,
        statuses: [...(prev.statuses || []), UserStatus.Banned],
      }));
    } else {
      delete queryParams.statuses;
    }
    handlePageChange(1);
  };

  const {
    data: assignedUsers,
    error: usersError,
    isLoading: usersLoading,
  } = useQuery({
    queryKey: [
      queryKey ? queryKey : "getUsers",
      queryParams,
      debouncedSearchTerm,
      organizationUId,
      debouncedRankFilterUids,
    ],

    queryFn: async () => {
      const response = referralAgents
        ? await getPaginatedReferralAgents(
            {
            ...queryParams,
              
              ...(debouncedSearchTerm && { name: debouncedSearchTerm }),
              ...(debouncedRankFilterUids.length > 0 && {
                rankUIds: debouncedRankFilterUids,
              }),
            },
            organizationUId
          )
        : await getPaginatedAssignedUsers(
            {
              ...queryParams,
              ...(debouncedSearchTerm && { name: debouncedSearchTerm }),
              ...(debouncedRankFilterUids.length > 0 && {
                rankUIds: debouncedRankFilterUids,
              }),
            },
            organizationUId
          );
      return response || [];
    },
    ...queryCommonFlags,
    staleTime: Infinity,
    refetchInterval: refetchInterval || 0,
    enabled,
    select: (
      data
    ): {
      users: MappedUserData[];
      count: number;
      page: number;
      pageSize: number;
    } => {
      return {
        users: data.data.results.map((user: UsersList) => ({
          id: user.uid,
          name: user.name,
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          configurations: user.configuration,
          color: user.color || "#FFF",
          banned: user?.banned,
          rankUId: user.rankId,
          subDepartment: user?.subDepartment || null,
          email: user?.email,
          phoneNumber: user?.phoneNumber,
          preferredUsername: user?.preferredUsername,
          teamSize: user?.teamSize,
          status: user?.status,
        })),
        count: data.data.total,
        page: data.data.page,
        pageSize: data.data.pageSize,
      };
    },
  });

  const updateParams = (newParams: Partial<UserQueryParams>) => {
    setQueryParams((prev) => ({
      ...prev,
      ...newParams,
    }));
  };

  return {
    assignedUsers: assignedUsers?.users,
    usersError,
    usersLoading,
    usersSearchTerm,
    setUsersSearchTerm,
    queryParams,
    updateParams,
    count: assignedUsers?.count,
    pagination: {
      page,
      pageSize,
      handlePageChange,
      handlePageSizeChange,
    },
    showBanned,
    handleShowBannedChange,
    rankFilterUids,
    setRankFilterUids,
  };
};

export default useUsersData;
